/**
 * The overview's reading guide and the closest human categories (DESIGN UD-33, 2026-10-19): what each aggregate number
 * on the page is, its scale, the thresholds that apply to the analysed document and the measured error rate at them.
 * Same conventions as `strings-explain.ts`: plain constants and small functions, no DOM; none may contain a word matched
 * by `BANNED_WORDS`, nor "classified", "probability" or "likelihood" (test/unit/reading-strings.test.ts). Every figure
 * arrives formatted from the model's data (`figuresFor`, the calibrated index, the human reference); nothing is computed
 * here. The status wording stays the engine's.
 */

import { SHORT_RATES_CROSS_VALIDATED } from './strings-explain.ts';

/** "n of m" with grouping, for counts the caller formats. */
const ofN = (k: string, n: string): string => `${k} of ${n}`;

export const READING = {
	title: 'How to read these numbers',
	hide: 'Hide',
	show: 'Show',
	toggleLabel: 'How to read these numbers',
	intro:
		'Each number on this page has its own scale, its own thresholds and its own error record. Only the machine-authorship index is calibrated; every other number is evidence that carries no error rate. None of them is a percentage of machine text, and none is proof of who wrote the text.',
	rows: {
		label: {
			what: 'What it is',
			scale: 'Scale',
			thresholds: 'Thresholds for this document',
			statuses: 'What each status means',
			record: 'Measured error record',
			not: 'What it is not',
		},
	},
	/** The machine-authorship index. */
	index: {
		title: 'Machine-authorship index (the large number on the left)',
		what: 'A calibrated score from the model’s reading of all the authorship channels together, passed through a calibration curve fitted separately for each length group. A higher index looks more like the machine text of the reference corpus.',
		scale:
			'0 to 100, held by the calibration between 1 and 99. It is an index: 50 does not mean half machine, and the same index can mean different things at different lengths, which is why the thresholds and the error record below are given for this document’s length.',
		document: (words: string, group: string, status: string) =>
			`This document: ${words} prose words, length group ${group}. Its status: ${status}.`,
		noGroup: (words: string, floor: string) =>
			`This document: ${words} prose words. The calibration starts at ${floor} prose words, so there is no index to compare with a threshold.`,
		referencePoint: (point: string) =>
			`Reference point ${point}: an index above it is flagged. It was set so that, in the strictest of the human categories of the reference corpus, 1% of the documents scored above it.`,
		shortPoint: (
			name: string,
			threshold: string,
			under: string,
			ceiling: string,
			human: string,
			esl: string,
			speeches: string,
			capped = true,
			crossValidated = false
		) =>
			`The ${name}, for texts under ${under} words: a text whose raw model score (the logit) is ${threshold} or more is flagged${capped ? ` even though its index stays at or under ${ceiling}` : ` by this point, whatever its index (the short-text calibration map stops at ${ceiling})`}. It is a looser bar than the reference point. Human documents under ${under} words flagged there: ${human}, among them ${esl} essays by writers of English as a second language and ${speeches} speeches.${crossValidated ? ` ${SHORT_RATES_CROSS_VALIDATED}` : ''}`,
		shortPointRate: (flagged: string, n: string, bound: string) =>
			`${ofN(flagged, n)} (at most ${bound})`,
		noBand:
			'This model has no abstention band, so the status never reads “in the abstention band”.',
		/** One line per status; `this` marks the document's own. */
		statusList: {
			flagged:
				'Flagged: the index is above the reference point (or, where the model has one for this length, the raw score reaches the short-text point). Look at the marked passages; a flag is a reason to read, not a finding.',
			'at-reference':
				'At the reference point: the index equals it. That is on the line, not above it, and is not flagged.',
			below:
				'Below: not flagged. A text that is not flagged says little about who wrote it; the record shows how much machine text of this length is flagged.',
			abstain:
				'In the abstention band: the index sits in a band around the reference point where the model gives no status.',
			'abstain-interval':
				'Abstains: the text-sampling interval spans the point, so re-sampling the document can move the index across the line. This applies only to length groups where the interval’s coverage reached its nominal level.',
			'not-available':
				'Not available: too little prose, or no calibrated model for the document’s language.',
		},
		thisStatus: 'This document',
		recordLead: (range: string, bin: string, points: string) =>
			`For documents of ${range} (reference-corpus bin ${bin}), at ${points}:`,
		rangeBetween: (lo: string, hi: string) => `${lo} to ${hi} words`,
		rangeOpen: (lo: string) => `${lo} words and more`,
		pointsReference: 'the reference point',
		pointsBoth: 'the reference point and the short-text point',
		recordHuman: (flagged: string, n: string, bound: string) =>
			`human documents flagged: ${ofN(flagged, n)} (at most ${bound}, one-sided 95% bound)`,
		recordMachine: (flagged: string, n: string, lower: string) =>
			`machine documents flagged: ${ofN(flagged, n)} (at least ${lower}, one-sided 95% bound)`,
		recordMachineUnmeasured: 'machine documents of this length: fewer than 30, unmeasured',
		recordBelowFloor: (floor: string) =>
			`No index is given below ${floor} prose words, so there is no error record for this length.`,
		recordNoBin:
			'The model card gives no error record for this length; a cell with fewer than 30 documents is unmeasured.',
		recordAll: (flagged: string, n: string, bound: string) =>
			`All lengths together, human documents flagged: ${ofN(flagged, n)} (at most ${bound}).`,
		byCategoryTitle: 'Human documents flagged, by category (all lengths)',
		byCategoryRow: (label: string, flagged: string, n: string, bound: string) =>
			`${label}: ${ofN(flagged, n)} (at most ${bound})`,
		modelCard: 'Model card',
		not: 'Not a percentage of machine text in the document, not proof, and not how sure to be that this document is machine-written, which depends on how much machine text you receive: the dial reads the index against the reference corpus, at a 50/50 prior, with its conditions.',
	},
	interval: {
		title: 'Text-sampling interval',
		what: 'How far the index moves when the document’s chapters, scenes or paragraphs are resampled.',
		scale: 'The same 0 to 100 scale as the index.',
		thresholds:
			'None of its own. It decides the status only for length groups where its coverage reached its nominal level (then a status can read “abstains”).',
		group: (gated: string) =>
			`This document’s length group: interval used for the status: ${gated}.`,
		not: 'It does not include the uncertainty of the calibration, and on the reference corpus it understates the variation between independent samples of the same text.',
	},
	humanRange: {
		title: 'Compared with human writing (the coloured cells on the channel list)',
		what: 'Where a channel’s score falls among human documents of about this length (half to twice its words) in the categories chosen on the Channels view.',
		scale: (within: string, far: string, min: string) =>
			`Colour bands by the share of those documents that score as far out as this text or further, on its side: within the human range at ${within}% or more (the middle 90%), outside it below ${within}%, far outside it at ${far}% or less. Fewer than ${min} human documents of this length: unmeasured.`,
		link: 'Compare channel by channel',
		see: 'Where to see it',
	},
	composite: {
		title: 'Seed-rules composite, section strip and chapters',
		what: 'The earlier summary from the seed’s rule set: a weighted sum of channel deviations mapped to a 0 to 100 scale, and the same rules applied per section and per chapter.',
		scale: '0 to 100 on its own scale, which is not the index’s scale.',
		thresholds: 'None. It is not calibrated and has no reference point.',
		not: 'It need not agree with the calibrated index, and a section strip that is flat near 0 means the seed rules see little evidence there, and says nothing about the calibrated index.',
	},
	coverage: {
		title: 'Evidence coverage',
		what: 'How much channel evidence stands behind the index, from the text length and the channels’ data weights.',
		scale: '0.10 to 1.00.',
		thresholds:
			'None. A low value means channels abstained or had little text; read the notices before the index.',
	},
	channels: {
		title: 'Channel values',
		what: 'Each channel reports on its own: how far one writing habit sits from the human reference, in the machine-like direction, given how much text supports it.',
		scale:
			'0 to 100, with a 90% interval and a data weight κ from 0 to 1. Around 20 is the human reference; a hollow marker means κ below 0.20, little evidence.',
		thresholds: 'None. Compare a value with the human range for this length on the Channels view.',
		not: 'A high value says a habit is strongly present, not that the text is machine-written.',
	},
	translation: {
		title: 'Translation index (the number on the right)',
		what: 'The uncalibrated score of the machine-translation channel MT-01: marks that machine translation into English leaves.',
		scale: '0 to 100.',
		thresholds: 'None. It has no reference point and no calibration.',
		record: (
			mt: string,
			mtN: string,
			mtLower: string,
			ht: string,
			htN: string,
			htBound: string,
			orig: string,
			origN: string,
			origBound: string,
			auroc: string,
			lo: string,
			hi: string,
			pairs: string
		) =>
			`The calibrated translation model (card t2-gam-4ec263a0) is kept as evidence. At its reference point it flagged ${ofN(mt, mtN)} Claude translations (at least ${mtLower}), ${ofN(ht, htN)} human translations (at most ${htBound}) and ${ofN(orig, origN)} human originals (at most ${origBound}). Comparing the ${pairs} Claude translations with the human translations of the same passages, it scored a Claude translation higher in ${auroc} of comparisons (95% interval ${lo} to ${hi}; 50% would mean no separation). Its gate G4 fails (see the card), so machine translation is not established.`,
		passed: 'This result’s translation model passed its gate; its model card gives the rates.',
		not: 'Writers working in a second language can score here too.',
	},
} as const;

export const CLOSEST = {
	title: 'Closest human categories',
	intro: (n: string) =>
		`Which kinds of human writing this text most resembles, channel by channel. For each category the page counts how many of the text’s measured channels fall inside that category’s range: the middle 90% of its documents of about this length, by the rule behind the colours on the Channels view. A channel is measured in a category when it has 30 or more human documents of this length there; the text has ${n} scored channels. The five categories with the highest share are listed.`,
	caveat:
		'This is resemblance by channel ranges. It is uncalibrated, carries no error rate and does not say who wrote the text; a machine-written text can resemble a human category too.',
	loading: 'Loading the human reference…',
	failed: 'The human reference could not be loaded.',
	none: 'No category has enough human documents of about this length to compare with.',
	rank: (i: string) => `${i}.`,
	fits: (within: string, measured: string) =>
		`inside its range on ${ofN(within, measured)} channels`,
	outside: (outside: string, far: string) => `${outside} outside, ${far} of them far outside`,
	pools: (min: string, max: string) =>
		min === max
			? `${min} human documents of this length per channel`
			: `${min} to ${max} human documents of this length per channel`,
	documents: (n: string) => `${n} documents in the reference`,
	signalsDistinguishing:
		'Channels inside this category’s range but outside the range of the ten original-English categories together (nearest its median first):',
	signalsNearest:
		'No channel is inside this category’s range and outside the range of the ten original-English categories together. The channels nearest its median:',
	signal: (
		score: string,
		lo: string,
		hi: string,
		median: string,
		pooledLo: string,
		pooledHi: string,
		outside: boolean
	) =>
		`score ${score}; category range ${lo} to ${hi} (median ${median}); the ten original-English categories together ${pooledLo} to ${pooledHi}${outside ? ', outside it' : ''}`,
	unmeasuredTitle: 'Not enough human documents of this length',
	unmeasuredBody:
		'These categories are left out because fewer than two thirds of the channels have 30 or more human documents of this length in them:',
	unmeasuredItem: (label: string, measured: string, scored: string, max: string) =>
		max === '0'
			? `${label}: no human documents of about this length`
			: `${label}: ${ofN(measured, scored)} channels measured, at most ${max} documents per channel`,
	fewChannels: (scored: string, total: string) =>
		`Only ${scored} of ${total} channels scored for this text, so this resemblance rests on few channels.`,
	termsLabel: 'Terms',
	openChannels: 'Compare channel by channel',
} as const;
